import '@sb/design-system';
/* remove the next import when everything is moved over to the new design system */
import '@sb/ui/global-tailwind.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { SharedPopperProvider } from '@sb/design-system/utility/shared-popper';
import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import AppProvider from '@sbrc/components/AppProvider';
import { AssemblyModeGuard } from '@sbrc/components/AssemblyModeGuard';
import { ControlPanelProtection } from '@sbrc/components/ControlPanelProtection';
import { ErrorBoundary } from '@sbrc/components/ErrorBoundary';
import RootLayout from '@sbrc/components/user-sessions/RootLayout';
import { pollSoftwareBuildData, pollUSBDrives } from '@sbrc/hooks';

import ErrorPage from './404';

const log = makeNamespacedLog('app');

/**
 * Attempts to import a module, and if it fails, attempts to reload the page.
 *
 * Targeted at dynamic module import errors.
 *
 * @param importer - The module function used to import.
 * @returns The imported module.
 */
async function safeImport<T>(importer: () => Promise<T>) {
  try {
    return await importer();
  } catch (error) {
    log.warn('safeImport', 'Module import failed. Attempt to reload page.', {
      error,
    });

    const modulePathString = importer.toString();
    const moduleMatch = modulePathString.match(/import\(['"](.+)['"]\)/);

    if (!(moduleMatch != null && moduleMatch[1] != null)) {
      throw new Error(
        `Failed to parse module path from importer (importer=${modulePathString}). Original error: ${error}`,
      );
    }

    const url = new URL(moduleMatch[1], window.location.origin);

    for (let i = 0; i < 5; i += 1) {
      await wait(1000 * i); // Backoff

      if (i > 0) {
        // Try initial import again with cache busting for network issues.
        url.searchParams.set('t', `${+new Date()}`); // cache busting
      }

      const newImportPath = url.pathname + url.search;

      try {
        return await import(/* @vite-ignore */ newImportPath);
      } catch (e) {
        log.error('safeImport', `safeImport failed (attempt ${i})`, {
          error: e,
        });
      }
    }

    throw error;
  }
}

// converter for react-router:
// - wrap page component in error boundary
// - named export `Component`
function page<T extends { default: React.ComponentType }>(
  importer: () => Promise<T>,
) {
  return async () => {
    const mdl = await safeImport(importer);

    return {
      ...mdl,
      Component() {
        return (
          <ErrorBoundary>
            <SharedPopperProvider>
              <mdl.default />
            </SharedPopperProvider>
          </ErrorBoundary>
        );
      },
    };
  };
}

// Same as page() but wrapped in AssemblyModeGuard so that switching submode
// out of LIVE while on this route shows the assembly tools UI instead of
// stranding the user on a now-irrelevant page.
function assemblyAwarePage<T extends { default: React.ComponentType }>(
  importer: () => Promise<T>,
) {
  return async () => {
    const mdl = await safeImport(importer);

    return {
      ...mdl,
      // eslint-disable-next-line react/no-multi-comp
      Component() {
        return (
          <ErrorBoundary>
            <SharedPopperProvider>
              <AssemblyModeGuard>
                <mdl.default />
              </AssemblyModeGuard>
            </SharedPopperProvider>
          </ErrorBoundary>
        );
      },
    };
  };
}

function controlPanelProtectedPage<T extends { default: React.ComponentType }>(
  importer: () => Promise<T>,
) {
  return async () => {
    const mdl = await safeImport(importer);

    return {
      ...mdl,
      // eslint-disable-next-line react/no-multi-comp
      Component() {
        return (
          <ErrorBoundary>
            <ControlPanelProtection>
              <mdl.default />
            </ControlPanelProtection>
          </ErrorBoundary>
        );
      },
    };
  };
}

// All routes nest under a single layout entry so the Routine Agent
// Chat panel (mounted in RootLayout) survives navigation. Per-route
// `errorElement` still applies; the layout's own errorElement
// catches errors that bubble past a child's boundary.
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      // root
      {
        path: '/',
        lazy: assemblyAwarePage(() => import('./main')),
        errorElement: <ErrorPage />,
      },

      // robby (assembly tools) device selection
      {
        path: '/arm',
        lazy: page(() => import('./arm')),
      },
      {
        path: '/grid',
        lazy: page(() => import('./grid')),
      },
      {
        path: '/joint/:serialNumber',
        lazy: page(() => import('./joint/[serialNumber]')),
      },
      {
        path: '/login',
        lazy: page(() => import('./login')),
      },
      {
        path: '/change-pin',
        lazy: page(() => import('./change-pin')),
      },
      {
        path: '/logout',
        lazy: page(() => import('./logout')),
      },
      {
        path: '/privacy',
        lazy: page(() => import('./privacy')),
      },
      {
        path: '/terms',
        lazy: page(() => import('./terms')),
      },
      {
        path: '/network',
        lazy: page(() => import('./network')),
      },

      // control-panel
      {
        path: '/control-panel',
        lazy: controlPanelProtectedPage(() => import('./control-panel')),
      },
      {
        path: '/control-panel/demos',
        lazy: controlPanelProtectedPage(() => import('./control-panel/demos')),
      },
      {
        path: '/control-panel/equipment-inspector',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/equipment-inspector'),
        ),
      },
      {
        path: '/control-panel/feature-flags/customer',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/feature-flags/customer'),
        ),
      },
      {
        path: '/control-panel/feature-flags/under-development',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/feature-flags/under-development'),
        ),
      },
      {
        path: '/control-panel/feature-flags/internal-testing',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/feature-flags/internal-testing'),
        ),
      },
      {
        path: '/control-panel/firmware-inspector',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/firmware-inspector'),
        ),
      },
      {
        path: '/control-panel/simulator-bot',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/simulator-bot'),
        ),
      },
      {
        path: '/control-panel/config-editor',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/config-editor'),
        ),
      },
      {
        path: '/control-panel/agent-settings',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/agent-settings'),
        ),
      },
      {
        path: '/control-panel/bug-reporting',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/bug-reporting'),
        ),
      },
      {
        path: '/control-panel/camera-viewer',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/camera-viewer'),
        ),
      },
      {
        path: '/control-panel/compliance-controller-panel',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/compliance-controller-panel'),
        ),
      },
      {
        path: '/control-panel/echo-bot-configuration',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/echo-bot-configuration'),
        ),
      },
      {
        path: '/control-panel/diagnostics',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/diagnostics'),
        ),
      },
      {
        path: '/control-panel/teleop-control',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/teleop-control'),
        ),
      },
      {
        path: '/control-panel/network-configuration',
        lazy: controlPanelProtectedPage(
          () => import('./control-panel/network-configuration'),
        ),
      },

      // demo
      {
        path: '/demo/ey',
        lazy: assemblyAwarePage(() => import('./demo/ey')),
      },
      {
        path: '/demo/skills',
        lazy: assemblyAwarePage(() => import('./demo/skills')),
      },

      // help
      {
        path: '/help',
        lazy: page(() => import('./help')),
      },
      {
        path: '/help/:article',
        lazy: page(() => import('./help/[article]')),
      },

      // robot
      {
        path: '/box',
        lazy: assemblyAwarePage(() => import('./box')),
      },
      {
        path: '/move',
        lazy: assemblyAwarePage(() => import('./move')),
      },
      {
        path: '/preflight',
        lazy: assemblyAwarePage(() => import('./preflight')),
      },
      {
        path: '/recover',
        lazy: assemblyAwarePage(() => import('./recover')),
      },
      {
        path: '/scene',
        lazy: assemblyAwarePage(() => import('./scene')),
      },

      // routines
      {
        path: '/routines',
        lazy: assemblyAwarePage(() => import('./routines')),
      },
      {
        path: '/routines/:routineID',
        lazy: assemblyAwarePage(() => import('./routines/[routineID]')),
      },

      // skills
      {
        path: '/skills/new',
        lazy: assemblyAwarePage(() => import('./skills/new')),
      },
      {
        path: '/skills/new/ai-vision',
        lazy: assemblyAwarePage(() => import('./skills/new/ai-vision')),
      },
      {
        path: '/skills/new/ai-vision/locate/:draftId',
        lazy: assemblyAwarePage(() => import('./skills/new/ai-vision/locate')),
      },
      {
        path: '/skills/new/ai-vision/classify',
        lazy: assemblyAwarePage(
          () => import('./skills/new/ai-vision/classify'),
        ),
      },
      {
        path: '/skills/new/ai-vision/coming-soon',
        lazy: assemblyAwarePage(
          () => import('./skills/new/ai-vision/coming-soon'),
        ),
      },
      {
        path: '/skills/new/ai-motion',
        lazy: assemblyAwarePage(() => import('./skills/new/ai-motion')),
      },
      {
        path: '/skills/new/ai-agent/:draftId',
        lazy: assemblyAwarePage(() => import('./skills/new/ai-agent')),
      },
      {
        path: '/skills/:skillID',
        lazy: assemblyAwarePage(() => import('./skills/[skillID]')),
      },

      // test platform ui
      {
        path: '/assembly-tests/test-runs/:testRunId',
        lazy: page(() => import('./assembly-tests')),
      },
      {
        path: '/assembly-tests/*',
        lazy: page(() => import('./assembly-tests')),
      },

      // pcb tests (factory_test_station)
      {
        path: '/pcb-tests',
        lazy: page(() => import('./pcb-tests')),
      },

      // calibrate arm
      {
        path: '/calibrate-arm',
        lazy: page(() => import('./calibrate-arm')),
      },
      {
        path: '/mpa',
        lazy: page(() => import('./calibrate-arm/mpa')),
      },

      //
      {
        path: '/training-data',
        lazy: assemblyAwarePage(() => import('./training-data')),
      },
      {
        path: '/training-data/:taskID',
        lazy: assemblyAwarePage(() => import('./training-data/[taskID]')),
      },

      // webxr
      {
        path: '/webxr',
        lazy: assemblyAwarePage(() => import('./webxr')),
      },

      {
        path: '/settings',
        lazy: assemblyAwarePage(() => import('./settings')),
      },
    ],
  },
]);

const root = createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    <AppProvider>
      <RouterProvider router={router} />
    </AppProvider>
  </StrictMode>,
);

pollSoftwareBuildData();
pollUSBDrives();
